import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { PageContainer, PageHeader } from "@/components/layout/page";
import { LoadingState } from "@/components/feedback/states";
import { useToast } from "@/components/feedback/toaster";
import { useConfirm } from "@/components/feedback/confirm-dialog";
import { useTenants } from "@/api/tenants";
import { useSessionStore } from "@/stores/session";
import { type TransferResult, useTenantTransfer, useTransferPlan } from "@/api/tenant-transfer";

export const Route = createFileRoute("/_app/system/transfer")({
  component: TenantTransfer,
});

const KINDS = ["Pipeline", "Connector", "Schedule"] as const;

function TenantTransfer() {
  const toast = useToast();
  const confirm = useConfirm();
  const tenants = useTenants();
  const currentTenant = useSessionStore((s) => s.tenant);
  const plan = useTransferPlan();
  const transfer = useTenantTransfer();

  const [kinds, setKinds] = useState<string[]>([...KINDS]);
  const [target, setTarget] = useState("");
  const [mode, setMode] = useState<"copy" | "move">("copy");
  const [withVersions, setWithVersions] = useState(true);
  const [result, setResult] = useState<TransferResult | null>(null);

  const targets = useMemo(
    () => (tenants.data ?? []).filter((t) => t.id !== currentTenant),
    [tenants.data, currentTenant],
  );

  if (tenants.isLoading) return <LoadingState />;

  const canRun = kinds.length > 0 && target && !transfer.isPending;

  async function runPlan() {
    setResult(null);
    await plan.mutateAsync({ kinds, targetTenant: target });
  }

  async function run() {
    const ok = await confirm({
      title: `${mode === "move" ? "Move" : "Copy"} ${kinds.join(", ")} to "${
        targets.find((t) => t.id === target)?.name ?? target
      }"?`,
      description:
        mode === "move"
          ? "The originals in this tenant will be deleted after a successful transfer. Connector secrets must be re-entered in the target."
          : "Connector secrets are redacted on transfer and must be re-entered in the target tenant.",
      destructive: mode === "move",
    });
    if (!ok) return;
    const res = await transfer.mutateAsync({ targetTenant: target, kinds, mode, withVersions });
    setResult(res);
    toast.success(`${res.mode === "move" ? "Moved" : "Copied"} ${res.resourceCount} resource(s)`);
  }

  const applied = result?.applied as Record<string, unknown> | undefined;
  const warnings = (applied?.warnings as string[] | undefined) ?? [];

  return (
    <PageContainer maxWidth={720}>
      <PageHeader
        title="Transfer between tenants"
        description="Copy or move pipeline, connector and schedule definitions from this tenant to another."
      />

      <Stack spacing={3}>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              What to transfer
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1, mb: 2 }}>
              {KINDS.map((k) => (
                <Chip
                  key={k}
                  label={k}
                  color={kinds.includes(k) ? "primary" : "default"}
                  variant={kinds.includes(k) ? "filled" : "outlined"}
                  onClick={() =>
                    setKinds((cur) => (cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]))
                  }
                />
              ))}
            </Stack>

            <TextField
              select
              size="small"
              label="Target tenant"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              sx={{ minWidth: 260 }}
            >
              {targets.length === 0 ? (
                <MenuItem value="" disabled>
                  No other tenants
                </MenuItem>
              ) : (
                targets.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))
              )}
            </TextField>

            <RadioGroup
              row
              value={mode}
              onChange={(e) => setMode(e.target.value as "copy" | "move")}
              sx={{ mt: 2 }}
            >
              <FormControlLabel value="copy" control={<Radio />} label="Copy (keep originals)" />
              <FormControlLabel
                value="move"
                control={<Radio />}
                label="Move (delete originals here)"
              />
            </RadioGroup>

            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={withVersions}
                  onChange={(e) => setWithVersions(e.target.checked)}
                />
              }
              label="Copy pipeline version history"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Run history (executions, dead letters, traces, anomalies) is tied to the source
              environment and is not transferred.
            </Typography>

            <Alert severity="info" sx={{ mt: 1 }}>
              Connector credentials are redacted on transfer — re-enter them in the target tenant
              after the transfer completes.
            </Alert>

            <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
              <Button variant="outlined" onClick={runPlan} disabled={!canRun || plan.isPending}>
                {plan.isPending ? "Checking…" : "Preview"}
              </Button>
              <Button
                variant="contained"
                color={mode === "move" ? "error" : "primary"}
                onClick={run}
                disabled={!canRun}
              >
                {transfer.isPending ? "Transferring…" : mode === "move" ? "Move" : "Copy"}
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {plan.data ? (
          <Card>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Preview — target tenant
              </Typography>
              <PlanList label="Create" items={plan.data.create} />
              <PlanList
                label="Update"
                items={plan.data.update.map((u) => `${u.resource} · ${u.changes.join(", ")}`)}
              />
              <Typography variant="caption" color="text.secondary">
                {plan.data.unchanged} unchanged · {plan.data.resourceCount ?? 0} resources read
              </Typography>
            </CardContent>
          </Card>
        ) : null}

        {result ? (
          <Card>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Done
              </Typography>
              <Box component="pre" sx={{ m: 0, fontSize: 12, whiteSpace: "pre-wrap" }}>
                {JSON.stringify(result.applied, null, 2)}
              </Box>
              {result.versionsCopied > 0 ? (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {result.versionsCopied} pipeline version(s) copied.
                </Typography>
              ) : null}
              {result.deletedFromSource.length > 0 ? (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Deleted from this tenant: {result.deletedFromSource.join(", ")}
                </Typography>
              ) : null}
              {warnings.length > 0 ? (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </Box>
                </Alert>
              ) : null}
            </CardContent>
          </Card>
        ) : null}
      </Stack>
    </PageContainer>
  );
}

function PlanList({ label, items }: { label: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 600 }}>
        {label} ({items.length})
      </Typography>
      <Box component="ul" sx={{ m: "2px 0 0", pl: 2.5 }}>
        {items.map((it, i) => (
          <Typography key={i} component="li" variant="body2" sx={{ fontFamily: "monospace" }}>
            {it}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}
